import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Button, Input, Select, Icon, Popover, InputNumber, message } from 'antd';
import { DEFAULT_FONT_FOR_LOCALISED_LANGUAGES } from '../../utils/helper';
import {
  ModalBody,
  ModalContainer,
  InputLabel,
  Container,
  InputContainer,
  AddNewCategory,
  PopupContainer,
  PopupActionContainer,
  PopupLabel,
  SaveButton,
  CancelButton,
  DropdownContainer,
  WarningLabel,
  InputFlexColumn,
  CategoryInputLabel,
} from './styles';
import { paperSizes } from '../../utils/complexPaperSizes';
import { getAllCategories, createCategory } from '../../utils/services/categoriesAPI';
import { AppStoreContext } from '../../context/appStoreContext';
import { getGroupPermission } from '../../utils/services/teamsAPI';
import { CREATE_NEW_FLOW_MODAL } from './translationValues';
import { useTranslation } from 'react-i18next';
import { createStandaloneFlow } from '../../utils/services/complexDocumentsAPI';
import { measurementConversion } from '../../utils/javascript';
import { TEMPLATE_NAME_CHAR_LIMIT } from '../../utils/constants/appConstants';
import { AvailableLanguagesDropdown, useSelectedTemplateLanguage } from './AvailableLanguagesDropdown';
import { isLanguageTranslationDisabled } from '../../utils/detectLanguageHelper';
import { useSessionContext } from '../../context/sessionContext';

const { Option } = Select;

const NAMESPACES = ['CREATE_NEW_FLOW_MODAL'];

const CreateFlowTemplate = props => {
  const { onSucess } = props;
  const { t, i18n } = useTranslation(NAMESPACES);
  const currentLanguage = i18n.language;

  const { userDetails } = useContext(AppStoreContext);
  const organisationSelector = useCallback(store => store.organisationDetails, []);
  const [organisationDetails] = useSessionContext(organisationSelector);
  const { languages, templateLanguage, setTemplateLanguage } = useSelectedTemplateLanguage();
  const userType = userDetails.userType;

  const [templateName, setTemplateName] = useState('');
  const [validTempName, setValidTempName] = useState(false);
  const [validCategName, setValidCategName] = useState(false);
  const [allCategories, setAllCategories] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState(null);
  const [savePopupVisible, setSavePopupVisible] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [warningText, setWarningText] = useState('');
  const [allTeams, setAllTeams] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [pageOrientation, setPageOrientation] = useState('portrait');
  const [paperSize, setPaperSize] = useState('a4');
  const [pageSize, setPageSize] = useState({
    height: null,
    width: null,
  });
  const [sizeUnit, setSizeUnit] = useState('mm');
  const [savingLoader, setSavingLoader] = useState(false);

  useEffect(() => {
    if (paperSize === 'custom') return;
    const sizes = paperSizes.filter(size => size.lowercaseName === paperSize)[0][pageOrientation];
    setPageSize({
      height: sizeUnit === 'mm' ? sizes.heightInMM : sizes.heightInPX,
      width: sizeUnit === 'mm' ? sizes.widthInMM : sizes.widthInPX,
    });
  }, [paperSize]);

  const handleUnitSizeChange = useCallback(
    value => {
      setSizeUnit(value);
      let height = measurementConversion(pageSize.height, value);
      let width = measurementConversion(pageSize.width, value);
      setPageSize({ height, width });
    },
    [pageSize],
  );

  const handleSizeChange = size => {
    setPaperSize(size);
  };

  const handlePageSizeChange = useCallback((value, key) => {
    setPageSize(prev => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    (async () => {
      const response = await getAllCategories(true);
      if (response && response.data) {
        let loggedUserCategory = response.data.filter(
          categoryObj => userType === categoryObj.categoryusertype,
        );
        setAllCategories(loggedUserCategory);
      }
      const teamData = await getGroupPermission(true);
      if (teamData?.data) {
        if (teamData.data.length === 1) {
          setSelectedGroup(teamData.data[0].rgId);
        }
        setAllTeams(teamData.data);
      } 
    })();
    // eslint-disable-next-line
  }, []);

  const saveNewFlow = async () => {
    let newFlow = {
      categoryId: selectedCategoryId,
      resourceGroupId: selectedGroup,
      flow: {
        isStandalone: true,
        flowName: templateName,
        orderNumber: 1,
        paperSize: paperSize,
        paperOrientation: pageOrientation,
        blocks: [],
        header: null,
        footer: null,
        style: {
          backgroundColor: "#ffffff",
          width: sizeUnit === 'mm' ? measurementConversion(pageSize.width, 'px') : pageSize.width,
          height: sizeUnit === 'mm' ? measurementConversion(pageSize.height, 'px') : pageSize.height,
          padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0
          },
          fontFamily: DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage),
          fontSize: 16,
        }
      }
    }
    if (isLanguageTranslationDisabled(organisationDetails) && templateLanguage) {
      newFlow.language = templateLanguage;
    }
    setSavingLoader(true);
    const response = await createStandaloneFlow(newFlow);
    if(response.statusCode === 2601) {
      onSucess(response.data.id);
      message.success(t(CREATE_NEW_FLOW_MODAL.NEW_PAGE_CREATED_SUCCESS_MESSAGE.key, CREATE_NEW_FLOW_MODAL.NEW_PAGE_CREATED_SUCCESS_MESSAGE.value));
    } else {
      if (response && response.errors.length > 0 && response.errors[0].code === 2601) {
        message.error(t(CREATE_NEW_FLOW_MODAL.PAGE_NAME_ALREADY_EXIST_ERROR_MESSAGE.key, CREATE_NEW_FLOW_MODAL.PAGE_NAME_ALREADY_EXIST_ERROR_MESSAGE.value));
      } else {
        message.error(t(CREATE_NEW_FLOW_MODAL.CREATEION_NEW_PAGE_ERROR_MESSAGE.key, CREATE_NEW_FLOW_MODAL.CREATEION_NEW_PAGE_ERROR_MESSAGE.value));
      }
    }
    setSavingLoader(false);
  };

  const setCategoryName = categoryName => {
    setNewCategoryName(categoryName.trimStart());
    if (categoryName.split(" ").join("").length) {
      setValidCategName(true);
    }
    else {
      setValidCategName(false)
    }
  };

  const handlePopupVisibleChange = visible => {
    setSavePopupVisible(visible);
  };

  const saveNewCategory = () => {
    setSavingCategory(true);
    let payload = {
      name: newCategoryName,
      type: 'element',
    };
    setSavePopupVisible(false);
    createNewCategory(payload);
  };

  const createNewCategory = async payload => {
    let categoryResponse = await createCategory(payload);
    if (categoryResponse && categoryResponse.errors && categoryResponse.errors.length === 0) {
      const savedCategory = {
        createdBy: null,
        createdOn: null,
        elements: null,
        id: categoryResponse.id,
        name: payload.name,
        parentId: 0,
        status: 'active',
        type: 'element',
        updatedBy: null,
        updatedOn: null,
      };
      setAllCategories(allCategories => [...allCategories, savedCategory]);
      setSavePopupVisible(false);
      setSelectedCategoryId(categoryResponse.id);
      setNewCategoryName(null);
      setSavingCategory(false);
    } else {
      setSavingCategory(false);
    }
  };

  const changeTempName = name => {
    setTemplateName(name.trimStart())
    if (name.split(" ").join("").length) {
      if(name.trimStart().length > TEMPLATE_NAME_CHAR_LIMIT) {
        setWarningText(t(CREATE_NEW_FLOW_MODAL.CHAR_LIMIT_EXCEEDED_ERROR.key, CREATE_NEW_FLOW_MODAL.CHAR_LIMIT_EXCEEDED_ERROR.value, { charLimitLength: TEMPLATE_NAME_CHAR_LIMIT }));
        setValidTempName(false);
        return;
      }
      setWarningText('');
      setValidTempName(true);
    }
    else {
      setWarningText(t(CREATE_NEW_FLOW_MODAL.ENTER_VALID_NAME_ERROR_MESSAGE.key, CREATE_NEW_FLOW_MODAL.ENTER_VALID_NAME_ERROR_MESSAGE.value));
      setValidTempName(false)
    }
  };

  return (
    <Container >
      <ModalBody>
        <ModalContainer >
          <InputContainer>
            <InputLabel>{t(CREATE_NEW_FLOW_MODAL.FLOW_NAME_LABEL.key, CREATE_NEW_FLOW_MODAL.FLOW_NAME_LABEL.value)}</InputLabel>
            <Input
              placeholder={t(CREATE_NEW_FLOW_MODAL.FLOW_NAME_PLACEHOLDER.key, CREATE_NEW_FLOW_MODAL.FLOW_NAME_PLACEHOLDER.value)}
              autoComplete="off"
              name="templateName"
              onChange={e => { changeTempName(e.target.value) }}
              size="large"
              value={templateName}
            />
            {warningText && <WarningLabel>{warningText}</WarningLabel>}
          </InputContainer>

          <InputContainer>
            <InputFlexColumn>
              <AddNewCategory>
                <CategoryInputLabel>{t(CREATE_NEW_FLOW_MODAL.SELECT_CATEGORY_LABEL.key, CREATE_NEW_FLOW_MODAL.SELECT_CATEGORY_LABEL.value)}</CategoryInputLabel>
                <Popover
                  content={
                    <PopupContainer>
                      <PopupLabel>{t(CREATE_NEW_FLOW_MODAL.CATEGORY_NAME_LABEL.key, CREATE_NEW_FLOW_MODAL.CATEGORY_NAME_LABEL.value)}</PopupLabel>
                      <Input
                        placeholder={t(CREATE_NEW_FLOW_MODAL.CATEGORY_NAME_PLACEHOLDER.key, CREATE_NEW_FLOW_MODAL.CATEGORY_NAME_PLACEHOLDER.value)}
                        autoComplete="off"
                        name="categoryName"
                        value={newCategoryName}
                        onChange={e => setCategoryName(e.target.value)}
                      />
                      <PopupActionContainer>
                        <CancelButton
                          size="default"
                          onClick={() => handlePopupVisibleChange(false)}
                        >
                          {t(CREATE_NEW_FLOW_MODAL.CANCEL_BUTTON.key, CREATE_NEW_FLOW_MODAL.CANCEL_BUTTON.value)}
                        </CancelButton>
                        <SaveButton
                          type="primary"
                          size="default"
                          onClick={() => saveNewCategory()}
                          disabled={!newCategoryName || !validCategName}
                          loading={savingCategory}
                        >
                          {savingCategory ? t(CREATE_NEW_FLOW_MODAL.SAVING_CATEGORY_LOADING_TEXT.key, CREATE_NEW_FLOW_MODAL.SAVING_CATEGORY_LOADING_TEXT.value) : t(CREATE_NEW_FLOW_MODAL.SAVE_BUTTON.key, CREATE_NEW_FLOW_MODAL.SAVE_BUTTON.value)}
                        </SaveButton>
                      </PopupActionContainer>
                    </PopupContainer>
                  }
                  placement="leftBottom"
                  trigger="click"
                  visible={savePopupVisible}
                  onVisibleChange={handlePopupVisibleChange}
                >
                  <Icon type="plus" /> {t(CREATE_NEW_FLOW_MODAL.ADD_NEW_ACTION.key, CREATE_NEW_FLOW_MODAL.ADD_NEW_ACTION.value)}
                </Popover>
              </AddNewCategory>
              <Select
                loading={!allCategories}
                showSearch
                placeholder={t(CREATE_NEW_FLOW_MODAL.SELECT_CATEGORY_PLACEHOLDER.key, CREATE_NEW_FLOW_MODAL.SELECT_CATEGORY_PLACEHOLDER.value)}
                optionFilterProp="children"
                value={selectedCategoryId ? selectedCategoryId : undefined}
                onChange={value => setSelectedCategoryId(value)}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {allCategories &&
                  allCategories.map(data => {
                    return (
                      <Option key={String(data.id)} value={data.id}>
                        {data.name}
                      </Option>
                    );
                  })}
              </Select>
            </InputFlexColumn>
          </InputContainer>

          {allTeams.length > 1 && (
            <InputContainer>
              <InputLabel>{t(CREATE_NEW_FLOW_MODAL.SELECT_TEAM_LABEL.key, CREATE_NEW_FLOW_MODAL.SELECT_TEAM_LABEL.value)}</InputLabel>
              <Select
                loading={!allTeams}
                showSearch
                placeholder={t(CREATE_NEW_FLOW_MODAL.SELECT_TEAM_PLACEHOLDER.key, CREATE_NEW_FLOW_MODAL.SELECT_TEAM_PLACEHOLDER.value)}
                optionFilterProp="children"
                value={selectedGroup || undefined}
                onChange={value => setSelectedGroup(value)}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {allTeams &&
                  allTeams.map(data => {
                    return (
                      <Option key={String(data.id)} value={data.rgId}>
                        {data.groupName}
                      </Option>
                    );
                  })}
              </Select>
            </InputContainer>
          )}
            <InputContainer className="flex-container">
              <DropdownContainer className="full-width">
                <InputLabel>
                  {t(
                    CREATE_NEW_FLOW_MODAL.COMPLEX_PAGE_SIZE_DROPDOWN_OPTION_LABEL.key,
                    CREATE_NEW_FLOW_MODAL.COMPLEX_PAGE_SIZE_DROPDOWN_OPTION_LABEL.value,
                  )}
                </InputLabel>
                <Select
                  value={paperSize}
                  onChange={handleSizeChange}
                >
                  <Option value="a4">A4 - 210 x 297 mm</Option>
                  <Option value={'us_letter'}>US Letter - 216 x 279 mm</Option>
                  <Option value={'us_legal'}>US Legal - 216 x 356 mm</Option>
                  <Option value="custom">
                    {t(
                      CREATE_NEW_FLOW_MODAL.CUSTOM_PAPER_SIZE_LABEL.key,
                      CREATE_NEW_FLOW_MODAL.CUSTOM_PAPER_SIZE_LABEL.value,
                    )}
                  </Option>
                </Select>
              </DropdownContainer>
            </InputContainer>
          {paperSize === 'custom' && (
            <div style={{ display: 'flex', gap: '24px' }}>
              <InputContainer>
                <InputLabel>
                  {t(
                    CREATE_NEW_FLOW_MODAL.CUSTOM_HEIGHT_LABEL.key,
                    CREATE_NEW_FLOW_MODAL.CUSTOM_HEIGHT_LABEL.value,
                  )}
                </InputLabel>
                <InputNumber
                  type="number"
                  min={1}
                  autoComplete="off"
                  size="large"
                  value={pageSize.height}
                  onChange={e => handlePageSizeChange(e, 'height')}
                />
              </InputContainer>
              <InputContainer>
                <InputLabel>
                  {t(
                    CREATE_NEW_FLOW_MODAL.CUSTOM_WIDTH_LABEL.key,
                    CREATE_NEW_FLOW_MODAL.CUSTOM_WIDTH_LABEL.value,
                  )}
                </InputLabel>
                <InputNumber
                  type="number"
                  min={1}
                  autoComplete="off"
                  size="large"
                  value={pageSize.width}
                  onChange={e => handlePageSizeChange(e, 'width')}
                />
              </InputContainer>              
              <InputContainer>
                <InputLabel>
                  {t(
                    CREATE_NEW_FLOW_MODAL.SIZE_IN_LABEL.key,
                    CREATE_NEW_FLOW_MODAL.SIZE_IN_LABEL.value,
                  )}
                </InputLabel>
                <Select value={sizeUnit} onChange={e => handleUnitSizeChange(e)}>
                  <Option value="px">px</Option>
                  <Option value="mm">mm</Option>
                </Select>
              </InputContainer>
            </div>
          )}
          <AvailableLanguagesDropdown
            languages={languages}
            templateLanguage={templateLanguage}
            setTemplateLanguage={setTemplateLanguage}
          />

          <InputContainer>
            <div className="form_btn">
              <Button
                disabled={!templateName || !validTempName || !selectedCategoryId || (allTeams.length > 1 && !selectedGroup)}
                key="submit"
                type="primary"
                onClick={saveNewFlow}
                loading={savingLoader}
              >
                {t(
                  CREATE_NEW_FLOW_MODAL.START_DESIGN_BUTTON.key,
                  CREATE_NEW_FLOW_MODAL.START_DESIGN_BUTTON.value,
                )}
              </Button>
            </div>
          </InputContainer>
        </ModalContainer>
      </ModalBody>
    </Container>
  );
};
export default CreateFlowTemplate;
